/**
 * Dashboard — /dashboard
 *
 * Owner dashboard. Auth: ForgeLine SSO via Supabase.
 * Emotional target: "control, not complexity."
 * Velvet warm-professional base. Crisp if only Recon calendars.
 *
 * Three sections:
 *   A. My Calendars — slug claim, business hours, notification email
 *   B. My Bookings — filtered list with cancel
 *   C. My Public Link — copyable link + QR code
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../supabase';
import { ownerApi } from '../api';
import { getTheme } from '../theme';
import {
  fmtDate,
  fmtDateTime,
  copyToClipboard,
  DAY_FULL_LABELS,
  DAY_KEYS,
} from '../utils';
import QRCode from 'qrcode';

const BOOKINGS_PER_PAGE = 50;

const DS = {
  page: {
    minHeight: '100vh',
    background: '#F5F3EF',
    fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  inner: {
    maxWidth: 900,
    margin: '0 auto',
    padding: '0 16px 80px',
  },
};

// ─── Integration badge ─────────────────────────────────────────────────────
function IntegrationBadge({ productName }) {
  const p = (productName || 'cal').toLowerCase();
  const map = {
    velvet: { label: 'Velvet', bg: '#FDF6E3', color: '#B8860B', border: '#E8D5A0' },
    recon:  { label: 'Recon',  bg: '#EFF6FF', color: '#1D4ED8', border: '#BFDBFE' },
    cal:    { label: 'Cal',    bg: '#EEF2FF', color: '#4361EE', border: '#C7D2FE' },
  };
  const s = map[p] || map.cal;
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 4,
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: '0.05em',
      background: s.bg,
      color: s.color,
      border: `1px solid ${s.border}`,
    }}>
      {s.label}
    </span>
  );
}

// ─── Status badge ──────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const map = {
    active:    { label: 'Active',    bg: '#DCFCE7', color: '#15803D' },
    paused:    { label: 'Paused',    bg: '#FEF9C3', color: '#854D0E' },
    confirmed: { label: 'Confirmed', bg: '#DCFCE7', color: '#15803D' },
    cancelled: { label: 'Cancelled', bg: '#FEE2E2', color: '#B91C1C' },
    pending:   { label: 'Pending',   bg: '#FEF9C3', color: '#854D0E' },
  };
  const s = map[status] || { label: status, bg: '#F3F4F6', color: '#374151' };
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 4,
      fontSize: 11,
      fontWeight: 600,
      background: s.bg,
      color: s.color,
    }}>
      {s.label}
    </span>
  );
}

// ─── Business hours editor ─────────────────────────────────────────────────
function BusinessHoursEditor({ hours, onChange }) {
  // hours is JSONB: { mon: [{open: '09:00', close: '17:00'}], ... }
  const [local, setLocal] = useState(() => {
    const base = {};
    DAY_KEYS.forEach(d => {
      base[d] = hours?.[d] || [];
    });
    return base;
  });

  const handleToggle = (day) => {
    setLocal(prev => {
      const updated = {
        ...prev,
        [day]: prev[day].length > 0 ? [] : [{ open: '09:00', close: '17:00' }],
      };
      onChange(updated);
      return updated;
    });
  };

  const handleWindowChange = (day, idx, field, val) => {
    setLocal(prev => {
      const windows = [...prev[day]];
      windows[idx] = { ...windows[idx], [field]: val };
      const updated = { ...prev, [day]: windows };
      onChange(updated);
      return updated;
    });
  };

  const addWindow = (day) => {
    setLocal(prev => {
      const updated = { ...prev, [day]: [...prev[day], { open: '09:00', close: '17:00' }] };
      onChange(updated);
      return updated;
    });
  };

  const removeWindow = (day, idx) => {
    setLocal(prev => {
      const windows = prev[day].filter((_, i) => i !== idx);
      const updated = { ...prev, [day]: windows };
      onChange(updated);
      return updated;
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {DAY_KEYS.map((key, i) => {
        const dayLabel = DAY_FULL_LABELS[i];
        const windows = local[key] || [];
        const open = windows.length > 0;

        return (
          <div key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '8px 0', borderBottom: '1px solid #EDE9E3' }}>
            <button
              type="button"
              className="cal-action-btn"
              onClick={() => handleToggle(key)}
              style={{
                width: 40,
                height: 22,
                borderRadius: 11,
                background: open ? '#B8860B' : '#D1C9B8',
                border: 'none',
                cursor: 'pointer',
                position: 'relative',
                transition: 'background 0.2s',
                flexShrink: 0,
                marginTop: 2,
              }}
            >
              <span style={{
                position: 'absolute',
                top: 2,
                left: open ? 20 : 2,
                width: 18,
                height: 18,
                borderRadius: '50%',
                background: '#fff',
                transition: 'left 0.2s',
              }} />
            </button>
            <div style={{ width: 72, fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: open ? '#2C1810' : '#A08060', fontWeight: 500, paddingTop: 2 }}>
              {dayLabel.slice(0, 3)}
            </div>
            {open ? (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                {windows.map((w, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="time"
                      value={w.open}
                      onChange={e => handleWindowChange(key, idx, 'open', e.target.value)}
                      style={S.timeInput}
                    />
                    <span style={{ color: '#A08060', fontSize: 12 }}>to</span>
                    <input
                      type="time"
                      value={w.close}
                      onChange={e => handleWindowChange(key, idx, 'close', e.target.value)}
                      style={S.timeInput}
                    />
                    {windows.length > 1 && (
                      <button type="button" className="cal-action-btn" onClick={() => removeWindow(key, idx)} style={S.removeBtn}>×</button>
                    )}
                  </div>
                ))}
                <button type="button" className="cal-action-btn" onClick={() => addWindow(key)} style={S.addWindowBtn}>
                  + Add window
                </button>
              </div>
            ) : (
              <span style={{ color: '#A08060', fontSize: 12, paddingTop: 3 }}>Closed</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Calendar card ─────────────────────────────────────────────────────────
function CalendarCard({ cal, token, onUpdated }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    slug: cal.slug || '',
    name: cal.name || '',
    notification_email: cal.notification_email || '',
    location_type: cal.location_type || 'video',
    location_types: cal.location_types && cal.location_types.length > 0
      ? cal.location_types
      : [cal.location_type || 'video'],
    location_address: cal.location_address || '',
    business_hours: cal.business_hours || {},
    status: cal.status || 'active',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const bookingUrl = cal.slug
    ? `https://cal.forgelinesystems.com/book/${cal.slug}`
    : null;

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      const updated = await ownerApi.updateCalendar(cal.id, {
        slug: form.slug || null,
        name: form.name,
        notification_email: form.notification_email || null,
        location_type: form.location_type || 'video',
        location_types: form.location_types || ['video'],
        location_address: form.location_address || null,
        business_hours: form.business_hours,
        status: form.status,
      }, token);
      onUpdated(updated);
      setEditing(false);
    } catch (err) {
      if (err.status === 409) {
        setError('That slug is already taken. Try a different one.');
      } else if (err.data?.error?.includes('reserved')) {
        setError('That slug is reserved. Try a different one.');
      } else {
        setError(err.message || 'Save failed. Try again.');
      }
    }
    setSaving(false);
  }

  async function handleCopy(text) {
    await copyToClipboard(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <div style={S.calCard}>
      {/* Card header */}
      <div className="cal-day-item" style={S.calCardHead} onClick={() => setExpanded(e => !e)}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={S.calName}>{cal.name}</span>
            <IntegrationBadge productName={cal.integration?.product_name} />
            <StatusBadge status={cal.status} />
          </div>
          {cal.slug && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
              <span style={S.slugDisplay}>/{cal.slug}</span>
              <button
                className="cal-action-btn"
                style={S.copyBtn}
                onClick={e => { e.stopPropagation(); handleCopy(`https://cal.forgelinesystems.com/book/${cal.slug}`); }}
              >
                {copied ? '✓' : '⎘'}
              </button>
            </div>
          )}
          <div style={S.bookingCount}>{cal.booking_count || 0} booking{cal.booking_count !== 1 ? 's' : ''}</div>
        </div>
        <span style={{ color: '#A08060', fontSize: 18, transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>›</span>
      </div>

      {/* Expanded editor */}
      {expanded && (
        <div style={S.calCardBody}>
          {!editing ? (
            <button className="cal-action-btn" style={S.editBtn} onClick={() => setEditing(true)}>Edit settings</button>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {/* Name */}
              <div>
                <label style={S.formLabel}>Calendar name</label>
                <input
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  style={S.formInput}
                />
              </div>

              {/* Slug */}
              <div>
                <label style={S.formLabel}>Public URL slug</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: '#A08060', fontSize: 13, fontFamily: "'DM Sans', sans-serif" }}>
                    cal.forgelinesystems.com/book/
                  </span>
                  <input
                    value={form.slug}
                    onChange={e => setForm(f => ({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
                    style={{ ...S.formInput, flex: 1 }}
                    placeholder="your-name"
                  />
                </div>
                <span style={{ color: '#A08060', fontSize: 12, fontFamily: "'DM Sans', sans-serif", marginTop: 4, display: 'block' }}>
                  Lowercase, hyphens only — e.g. callie-james
                </span>
              </div>

              {/* Notification email */}
              <div>
                <label style={S.formLabel}>Notification email</label>
                <input
                  type="email"
                  value={form.notification_email}
                  onChange={e => setForm(f => ({ ...f, notification_email: e.target.value }))}
                  style={S.formInput}
                  placeholder="you@email.com"
                />
                <span style={{ color: '#A08060', fontSize: 12, fontFamily: "'DM Sans', sans-serif", marginTop: 4, display: 'block' }}>
                  You'll receive an email when someone books
                </span>
              </div>

              {/* Meeting types — owner enables which formats they accept */}
              <div>
                <label style={S.formLabel}>Meeting types offered</label>
                <p style={{ fontSize: 12, color: '#A08060', marginTop: 0, marginBottom: 10, fontFamily: "'DM Sans', sans-serif" }}>
                  Choose which formats bookers can request
                </p>
                {[
                  { 
                    value: 'video', label: 'Video call',
                    icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 10l4.553-2.069A1 1 0 0 1 21 8.845v6.31a1 1 0 0 1-1.447.894L15 14M3 8a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8z"/></svg>
                  },
                  {
                    value: 'phone', label: 'Phone call',
                    icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.07 11 19.79 19.79 0 0 1 1.05 2.5 2 2 0 0 1 3 .35h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 7.91a16 16 0 0 0 6.1 6.1l.41-.41a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 21 16z"/></svg>
                  },
                  {
                    value: 'in_person', label: 'In person',
                    icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                  },
                ].map(opt => {
                  const checked = (form.location_types || ['video']).includes(opt.value);
                  return (
                    <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          const current = form.location_types || ['video'];
                          const next = checked
                            ? current.filter(v => v !== opt.value)
                            : [...current, opt.value];
                          // Don't allow deselecting all
                          if (next.length > 0) setForm(f => ({ ...f, location_types: next }));
                        }}
                        style={{ width: 16, height: 16, accentColor: '#B8860B', cursor: 'pointer' }}
                      />
                      <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: '#3D2B1A' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>{opt.icon}{opt.label}</span>
                      </span>
                    </label>
                  );
                })}
              </div>

              {/* Address field — only shown when in_person is enabled */}
              {(form.location_types || []).includes('in_person') && (
                <div style={{ marginTop: 10 }}>
                  <label style={S.formLabel}>Meeting address</label>
                  <input
                    type="text"
                    placeholder="123 Main St, Boise, ID 83702"
                    value={form.location_address || ''}
                    onChange={e => setForm(f => ({ ...f, location_address: e.target.value }))}
                    style={S.formInput}
                  />
                  <span style={{ color: '#A08060', fontSize: 12, fontFamily: "'DM Sans', sans-serif", marginTop: 4, display: 'block' }}>
                    Shown to bookers as an Apple Maps link on their confirmation
                  </span>
                </div>
              )}

              {/* Status toggle */}
              <div>
                <label style={S.formLabel}>Status</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {['active', 'paused'].map(s => (
                    <button
                      key={s}
                      type="button"
                      className="cal-action-btn"
                      style={{
                        padding: '8px 16px',
                        borderRadius: 6,
                        border: `1px solid ${form.status === s ? '#B8860B' : '#E0D8C8'}`,
                        background: form.status === s ? '#B8860B10' : '#FFFFFF',
                        color: form.status === s ? '#B8860B' : '#6B5540',
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: 13,
                        fontWeight: form.status === s ? 600 : 400,
                        cursor: 'pointer',
                        textTransform: 'capitalize',
                      }}
                      onClick={() => setForm(f => ({ ...f, status: s }))}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              {/* Business hours */}
              <div>
                <label style={S.formLabel}>Business hours</label>
                <BusinessHoursEditor
                  hours={form.business_hours}
                  onChange={bh => setForm(f => ({ ...f, business_hours: bh }))}
                />
              </div>

              {error && <div style={S.inlineError}>{error}</div>}

              <div style={{ display: 'flex', gap: 8 }}>
                <button className="cal-btn-primary" style={S.saveBtn} disabled={saving} onClick={handleSave}>
                  {saving ? 'Saving…' : 'Save changes'}
                </button>
                <button
                  className="cal-btn-secondary"
                  style={S.cancelEditBtn}
                  onClick={() => { setEditing(false); setError(null); }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── My Bookings ──────────────────────────────────────────────────────────
function MyBookings({ token, calendars }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calFilter, setCalFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [cancelConfirm, setCancelConfirm] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const data = await ownerApi.getBookings({
        calendar_id: calFilter || undefined,
        status: statusFilter || undefined,
        limit: BOOKINGS_PER_PAGE,
        offset,
      }, token);
      setBookings(data.bookings || []);
      setTotal(data.total || 0);
    } catch {
      // silent fail — show empty state
    }
    setLoading(false);
  }, [token, calFilter, statusFilter, offset]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  async function handleCancel(id) {
    setCancelling(true);
    try {
      await ownerApi.cancelBooking(id, token);
      setCancelConfirm(null);
      fetchBookings();
    } catch {
      // show inline error
    }
    setCancelling(false);
  }

  return (
    <section style={S.section}>
      <h2 style={S.sectionTitle}>My Bookings</h2>

      {/* Filters */}
      <div style={S.filterRow}>
        <select
          style={S.filterSelect}
          value={calFilter}
          onChange={e => { setCalFilter(e.target.value); setOffset(0); }}
        >
          <option value="">All calendars</option>
          {calendars.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <select
          style={S.filterSelect}
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setOffset(0); }}
        >
          <option value="">All statuses</option>
          <option value="confirmed">Confirmed</option>
          <option value="cancelled">Cancelled</option>
          <option value="pending">Pending</option>
        </select>
      </div>

      {/* Booking list */}
      {loading ? (
        <div style={S.loadingText}>Loading bookings…</div>
      ) : bookings.length === 0 ? (
        <div style={S.emptyState}>No bookings yet.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {bookings.map(b => (
            <div key={b.id} style={S.bookingRow}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: '#2C1810', fontFamily: "'DM Sans', sans-serif" }}>
                  {b.participant?.name || 'Guest'}
                </div>
                <div style={{ fontSize: 12, color: '#6B5540', fontFamily: "'DM Sans', sans-serif", marginTop: 2 }}>
                  {b.participant?.email}
                </div>
                <div style={{ fontSize: 12, color: '#A08060', fontFamily: "'DM Sans', sans-serif", marginTop: 2 }}>
                  {b.starts_at ? fmtDateTime(b.starts_at) : '—'}
                  {b.calendar_name && ` · ${b.calendar_name}`}
                </div>
                <div style={{ marginTop: 4, display: 'flex', gap: 6, alignItems: 'center' }}>
                  <StatusBadge status={b.status} />
                  {b.id && (
                    <span style={{ fontSize: 10, color: '#A08060', fontFamily: 'monospace' }}>
                      #{b.id.slice(-8)}
                    </span>
                  )}
                </div>
              </div>
              {b.status !== 'cancelled' && (
                <div>
                  {cancelConfirm === b.id ? (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        className="cal-btn-primary"
                        style={S.cancelConfirmBtn}
                        disabled={cancelling}
                        onClick={() => handleCancel(b.id)}
                      >
                        {cancelling ? '…' : 'Yes, cancel'}
                      </button>
                      <button className="cal-btn-secondary" style={S.cancelDismissBtn} onClick={() => setCancelConfirm(null)}>
                        Keep
                      </button>
                    </div>
                  ) : (
                    <button className="cal-action-btn" style={S.cancelBookingBtn} onClick={() => setCancelConfirm(b.id)}>
                      Cancel
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {total > BOOKINGS_PER_PAGE && (
        <div style={S.pagination}>
          <button
            className="cal-action-btn"
            style={S.pageBtn(offset === 0)}
            disabled={offset === 0}
            onClick={() => setOffset(o => Math.max(0, o - BOOKINGS_PER_PAGE))}
          >
            ← Previous
          </button>
          <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: '#6B5540' }}>
            {offset + 1}–{Math.min(offset + BOOKINGS_PER_PAGE, total)} of {total}
          </span>
          <button
            className="cal-action-btn"
            style={S.pageBtn(offset + BOOKINGS_PER_PAGE >= total)}
            disabled={offset + BOOKINGS_PER_PAGE >= total}
            onClick={() => setOffset(o => o + BOOKINGS_PER_PAGE)}
          >
            Next →
          </button>
        </div>
      )}
    </section>
  );
}

// ─── Public link + QR ─────────────────────────────────────────────────────
function MyPublicLink({ calendars }) {
  const canvasRef = useRef(null);
  const [copied, setCopied] = useState(false);

  // Find first calendar with a slug
  const primary = calendars.find(c => c.slug);
  const url = primary ? `https://cal.forgelinesystems.com/book/${primary.slug}` : null;

  useEffect(() => {
    if (url && canvasRef.current) {
      QRCode.toCanvas(canvasRef.current, url, {
        width: 180,
        margin: 2,
        color: { dark: '#2C1810', light: '#FAF7F2' },
      });
    }
  }, [url]);

  async function handleCopy() {
    if (!url) return;
    await copyToClipboard(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  if (!url) return null;

  return (
    <section style={S.section}>
      <h2 style={S.sectionTitle}>My Public Link</h2>
      <div style={S.linkCard}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={S.publicUrl}>{url}</span>
          <button className="cal-btn-primary" style={S.copyBtn2} onClick={handleCopy}>
            {copied ? '✓ Copied' : 'Copy link'}
          </button>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            style={S.previewLink}
          >
            Preview ↗
          </a>
        </div>
        <div style={{ marginTop: 20, display: 'flex', justifyContent: 'center' }}>
          <canvas ref={canvasRef} style={{ borderRadius: 8, border: '1px solid #E0D8C8' }} />
        </div>
        <p style={{ textAlign: 'center', fontSize: 12, color: '#A08060', fontFamily: "'DM Sans', sans-serif", marginTop: 8 }}>
          Scan to open your booking page
        </p>
      </div>
    </section>
  );
}

// ─── Auth gate ─────────────────────────────────────────────────────────────
function AuthGate({ onSession }) {
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        onSession(session);
      }
      setLoading(false);
    });

    // Listen for auth state changes (magic link redirect)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) onSession(session);
    });

    return () => subscription.unsubscribe();
  }, [onSession]);

  async function handleSendLink(e) {
    e.preventDefault();
    if (!email.trim()) return;
    setSending(true);
    setError(null);
    try {
      const { error: authError } = await supabase.auth.signInWithOtp({
        email: email.trim(),
        options: {
          emailRedirectTo: `${window.location.origin}/dashboard`,
        },
      });
      if (authError) throw authError;
      setSent(true);
    } catch (err) {
      setError(err.message || 'Failed to send magic link');
    }
    setSending(false);
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontFamily: "'DM Sans', sans-serif", color: '#A08060' }}>Checking auth…</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh' }}>
      <div style={{ maxWidth: 400, width: '100%', padding: '40px 24px', textAlign: 'center' }}>
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginBottom: '24px', opacity: 0.6 }}>
          <rect x="4" y="8" width="32" height="28" rx="3" stroke="currentColor" strokeWidth="2" fill="none"/>
          <line x1="4" y1="16" x2="36" y2="16" stroke="currentColor" strokeWidth="2"/>
          <line x1="13" y1="4" x2="13" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          <line x1="27" y1="4" x2="27" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
        <h1 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: '2rem', fontWeight: 400, color: '#2C1810', margin: '0 0 8px' }}>
          Your schedule is ready.
        </h1>
        <p style={{ fontFamily: "'DM Sans', sans-serif", color: '#6B5540', marginBottom: 32, lineHeight: 1.6 }}>
          Sign in to see your bookings and manage your calendar.
        </p>

        {sent ? (
          <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 10, padding: '20px 24px', color: '#15803D', fontFamily: "'DM Sans', sans-serif", fontSize: 14, lineHeight: 1.6 }}>
            <strong>Check your email.</strong> We've sent you a sign-in link.
          </div>
        ) : (
          <form onSubmit={handleSendLink} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="your@email.com"
              style={{
                padding: '13px 16px',
                border: '1px solid #E0D8C8',
                borderRadius: 8,
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 15,
                color: '#2C1810',
                background: '#FAF7F2',
                outline: 'none',
                width: '100%',
                boxSizing: 'border-box',
              }}
              autoComplete="email"
              autoFocus
            />
            {error && <div style={{ color: '#8B2500', fontSize: 13, fontFamily: "'DM Sans', sans-serif" }}>{error}</div>}
            <button
              type="submit"
              className="cal-btn-primary"
              disabled={sending}
              style={{
                background: '#B8860B',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: 8,
                padding: '13px 24px',
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 14,
                fontWeight: 600,
                cursor: sending ? 'not-allowed' : 'pointer',
                opacity: sending ? 0.7 : 1,
              }}
            >
              {sending ? 'Sending…' : 'Send sign-in link'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// ─── Main Dashboard ────────────────────────────────────────────────────────
export default function Dashboard() {
  const [session, setSession] = useState(null);
  const [calendars, setCalendars] = useState([]);
  const [loadingCals, setLoadingCals] = useState(false);
  const [tab, setTab] = useState('calendars');
  const [profile, setProfile] = useState(null);

  // Determine dominant theme from calendars
  const dominantProduct = calendars.length > 0
    ? (calendars.every(c => c.integration?.product_name === 'recon') ? 'recon' : 'velvet')
    : 'velvet';
  const T = getTheme(dominantProduct);

  // Inject fonts
  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;600&family=DM+Sans:wght@300;400;500;600&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }, []);

  const handleSession = useCallback(async (s) => {
    setSession(s);
    localStorage.setItem('cal_supabase_token', s.access_token);
    setLoadingCals(true);
    try {
      const [data, prof] = await Promise.all([
        ownerApi.getCalendars(s.access_token),
        ownerApi.getProfile(s.access_token).catch(() => null),
      ]);
      setCalendars(Array.isArray(data) ? data : (data.calendars || []));
      if (prof) setProfile(prof);
    } catch {
      // silent
    }
    setLoadingCals(false);
  }, []);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    localStorage.removeItem('cal_supabase_token');
    setSession(null);
    setCalendars([]);
  };

  const handleCalUpdated = useCallback((updated) => {
    setCalendars(prev => prev.map(c => c.id === updated.id ? { ...c, ...updated } : c));
  }, []);

  if (!session) {
    return (
      <div style={DS.page}>
        <AuthGate onSession={handleSession} />
      </div>
    );
  }

  return (
    <div style={DS.page}>
      {/* Nav bar */}
      <nav style={S.nav}>
        <div style={S.navInner}>
          <span style={S.navBrand}>Cal</span>
          <div style={{ display: 'flex', gap: 4 }}>
            {[
              { id: 'calendars', label: 'My Calendars' },
              { id: 'bookings', label: 'My Bookings' },
              { id: 'link', label: 'My Public Link' },
              { id: 'availability', label: 'Availability' },
              { id: 'profile', label: 'Profile' },
            ].map(item => (
              <button
                key={item.id}
                className="cal-action-btn"
                style={S.navBtn(tab === item.id)}
                onClick={() => setTab(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>
          <button className="cal-action-btn" style={S.signOutBtn} onClick={handleSignOut}>Sign out</button>
        </div>
      </nav>

      <div style={DS.inner}>
        {tab === 'calendars' && (
          <section style={S.section}>
            <h2 style={S.sectionTitle}>My Calendars</h2>
            {loadingCals ? (
              <div style={S.loadingText}>Loading calendars…</div>
            ) : calendars.length === 0 ? (
              <div style={S.emptyState}>No calendars found. Your calendars will appear here once your account is connected to a Cal-enabled service.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {calendars.map(cal => (
                  <CalendarCard
                    key={cal.id}
                    cal={cal}
                    token={session.access_token}
                    onUpdated={handleCalUpdated}
                  />
                ))}
              </div>
            )}
          </section>
        )}

        {tab === 'bookings' && (
          <MyBookings token={session.access_token} calendars={calendars} />
        )}

        {tab === 'link' && (
          <MyPublicLink calendars={calendars} />
        )}

        {tab === 'availability' && (
          <AvailabilitySettings
            token={session.access_token}
            calendarId={calendars[0]?.id}
            calendars={calendars}
            T={T}
          />
        )}

        {tab === 'profile' && (
          <ProfileSettings
            token={session.access_token}
            profile={profile}
            onUpdated={(p) => setProfile(p)}
          />
        )}
      </div>
    </div>
  );
}

// ─── AvailabilitySettings component ──────────────────────────────────────────
function AvailabilitySettings({ token, calendarId: defaultCalendarId, calendars, T }) {
  const [selectedCalId, setSelectedCalId] = React.useState(defaultCalendarId || '');
  const calendarId = selectedCalId || defaultCalendarId;
  const [blocks, setBlocks] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [form, setForm] = React.useState({ date: '', startTime: '09:00', endTime: '17:00', reason: '', allDay: true });
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (!calendarId) { setLoading(false); return; }
    setLoading(true);
    ownerApi.getBlocks(token, calendarId)
      .then(data => setBlocks(Array.isArray(data) ? data : (data.blocks || [])))
      .catch(() => setBlocks([]))
      .finally(() => setLoading(false));
  }, [calendarId]);

  async function handleAdd() {
    if (!form.date) { setError('Pick a date'); return; }
    if (!calendarId) { setError('No calendar selected'); return; }
    setError('');
    setSaving(true);
    try {
      const dateStr = form.date;
      const starts_at = form.allDay
        ? new Date(`${dateStr}T00:00:00`).toISOString()
        : new Date(`${dateStr}T${form.startTime}:00`).toISOString();
      const ends_at = form.allDay
        ? new Date(`${dateStr}T23:59:59`).toISOString()
        : new Date(`${dateStr}T${form.endTime}:00`).toISOString();

      const block = await ownerApi.createBlock(token, {
        calendar_id: calendarId,
        starts_at,
        ends_at,
        reason: form.reason || null,
      });
      setBlocks(prev => [...prev, block].sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at)));
      setForm({ date: '', startTime: '09:00', endTime: '17:00', reason: '', allDay: true });
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(blockId) {
    try {
      await ownerApi.deleteBlock(token, blockId, calendarId);
      setBlocks(prev => prev.filter(b => b.id !== blockId));
    } catch (e) {
      console.error('Delete block failed:', e.message);
    }
  }

  function formatBlockLabel(block) {
    const start = new Date(block.starts_at);
    const end   = new Date(block.ends_at);
    const dateStr = start.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    const isAllDay = start.getHours() === 0 && start.getMinutes() === 0
      && end.getHours() === 23 && end.getMinutes() >= 59;
    if (isAllDay) return `${dateStr} \u2014 All day`;
    const timeOpts = { hour: 'numeric', minute: '2-digit', hour12: true };
    return `${dateStr}, ${start.toLocaleTimeString('en-US', timeOpts)} \u2013 ${end.toLocaleTimeString('en-US', timeOpts)}`;
  }

  const inputStyle = {
    padding: '8px 10px',
    border: `1px solid ${T?.border || '#E0D8C8'}`,
    borderRadius: 7,
    fontSize: 13,
    fontFamily: "'DM Sans', sans-serif",
    color: T?.text || '#3D2B1A',
    background: '#fff',
  };

  return (
    <section style={S.section}>
      <h2 style={S.sectionTitle}>Availability</h2>

      {/* Calendar selector (if multiple) */}
      {calendars && calendars.length > 1 && (
        <div style={{ marginBottom: 20 }}>
          <select
            style={S.filterSelect}
            value={selectedCalId || defaultCalendarId || ''}
            onChange={e => setSelectedCalId(e.target.value)}
          >
            {calendars.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
      )}

      <div style={{ maxWidth: 540 }}>
        <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, color: T?.text || '#3D2B1A', marginBottom: 4, marginTop: 0 }}>
          Block time off
        </h3>
        <p style={{ fontSize: 13, color: T?.textSecondary || '#8B6F47', marginBottom: 20, fontFamily: "'DM Sans', sans-serif", marginTop: 0 }}>
          Blocked times won't appear as available to bookers.
        </p>

        {/* Add block form */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24, padding: 16, background: '#FAF7F2', borderRadius: 10, border: `1px solid ${T?.border || '#E0D8C8'}` }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="date"
              value={form.date}
              min={new Date().toISOString().split('T')[0]}
              onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
              style={{ ...inputStyle, flex: '1 1 140px' }}
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontFamily: "'DM Sans', sans-serif", color: T?.text || '#3D2B1A', whiteSpace: 'nowrap' }}>
              <input
                type="checkbox"
                checked={form.allDay}
                onChange={e => setForm(f => ({ ...f, allDay: e.target.checked }))}
                style={{ accentColor: '#B8860B' }}
              />
              All day
            </label>
          </div>

          {!form.allDay && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="time" value={form.startTime} onChange={e => setForm(f => ({ ...f, startTime: e.target.value }))} style={{ ...inputStyle, flex: 1 }} />
              <span style={{ color: T?.textSecondary || '#8B6F47', fontSize: 13 }}>to</span>
              <input type="time" value={form.endTime} onChange={e => setForm(f => ({ ...f, endTime: e.target.value }))} style={{ ...inputStyle, flex: 1 }} />
            </div>
          )}

          <input
            type="text"
            placeholder="Reason (optional \u2014 only you see this)"
            value={form.reason}
            onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
            style={{ ...inputStyle }}
          />

          {error && <p style={{ color: '#DC2626', fontSize: 12, margin: 0, fontFamily: "'DM Sans', sans-serif" }}>{error}</p>}

          <button
            onClick={handleAdd}
            disabled={saving || !form.date}
            style={{
              padding: '9px 18px', borderRadius: 8, border: 'none',
              background: saving || !form.date ? '#D0C4B0' : '#B8860B',
              color: '#fff', fontSize: 13, fontWeight: 600,
              fontFamily: "'DM Sans', sans-serif", cursor: saving || !form.date ? 'not-allowed' : 'pointer',
              alignSelf: 'flex-start',
            }}
          >
            {saving ? 'Blocking\u2026' : 'Block this time'}
          </button>
        </div>

        {/* Active blocks list */}
        {loading ? (
          <p style={{ color: T?.textSecondary || '#8B6F47', fontSize: 13, fontFamily: "'DM Sans', sans-serif" }}>Loading\u2026</p>
        ) : blocks.length === 0 ? (
          <p style={{ color: T?.textSecondary || '#8B6F47', fontSize: 13, fontFamily: "'DM Sans', sans-serif" }}>
            No blocked times. You're fully open for the next 90 days.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <p style={{ fontSize: 12, fontWeight: 600, color: T?.textSecondary || '#8B6F47', fontFamily: "'DM Sans', sans-serif", marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Upcoming blocks
            </p>
            {blocks.map(block => (
              <div key={block.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#fff', borderRadius: 8, border: `1px solid ${T?.border || '#E0D8C8'}` }}>
                <div>
                  <span style={{ fontSize: 13, fontFamily: "'DM Sans', sans-serif", color: T?.text || '#3D2B1A', fontWeight: 500 }}>
                    {formatBlockLabel(block)}
                  </span>
                  {block.reason && (
                    <span style={{ display: 'block', fontSize: 11, color: T?.textSecondary || '#8B6F47', marginTop: 2, fontFamily: "'DM Sans', sans-serif" }}>
                      {block.reason}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => handleDelete(block.id)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#DC2626', fontSize: 18, lineHeight: 1, padding: '2px 4px' }}
                  title="Remove block"
                >
                  \u00d7
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

// ─── ProfileSettings component ───────────────────────────────────────────────
function ProfileSettings({ token, profile, onUpdated }) {
  const [photoUrl, setPhotoUrl] = React.useState(profile?.photo_url || '');
  const [displayName, setDisplayName] = React.useState(profile?.name || '');
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [error, setError] = React.useState(null);

  const handleSave = async () => {
    setSaving(true); setSaved(false); setError(null);
    try {
      await ownerApi.updateProfile({
        name: displayName || undefined,
        photo_url: photoUrl || null,
      }, token);
      onUpdated({ name: displayName, photo_url: photoUrl });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e.message || 'Save failed');
    }
    setSaving(false);
  };

  return (
    <section style={{ maxWidth: 480, margin: '0 auto', padding: '32px 16px' }}>
      <h2 style={{ fontFamily: 'DM Sans, Inter, sans-serif', fontSize: '1.1rem', fontWeight: 600, marginBottom: 24, color: '#1A1A1A' }}>
        Public Profile
      </h2>

      {/* Avatar preview */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 28 }}>
        {photoUrl ? (
          <img src={photoUrl} alt="Preview"
            style={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover', border: '2px solid #E5E7EB' }} />
        ) : (
          <div style={{
            width: 72, height: 72, borderRadius: '50%',
            background: '#D9770620', color: '#D97706',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '1.4rem', fontWeight: 700, fontFamily: 'DM Sans, sans-serif',
          }}>
            {(displayName || profile?.name || '').split(' ').filter(Boolean).map(w => w[0]).slice(0,2).join('').toUpperCase() || '?'}
          </div>
        )}
        <div>
          <div style={{ fontSize: 13, fontWeight: 500, color: '#1A1A1A', marginBottom: 2 }}>
            {displayName || profile?.name || 'Your name'}
          </div>
          <div style={{ fontSize: 12, color: '#6B7280' }}>
            Shown on your booking page
          </div>
        </div>
      </div>

      {/* Display name */}
      <div style={{ marginBottom: 20 }}>
        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>
          Display name
        </label>
        <input
          type="text"
          value={displayName}
          onChange={e => setDisplayName(e.target.value)}
          placeholder="Erik Wangenheim"
          style={{
            width: '100%', padding: '10px 12px', borderRadius: 8,
            border: '1.5px solid #E5E7EB', fontFamily: 'DM Sans, sans-serif',
            fontSize: 14, color: '#1A1A1A', background: '#fff', boxSizing: 'border-box',
          }}
        />
      </div>

      {/* Photo URL */}
      <div style={{ marginBottom: 24 }}>
        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>
          Profile photo URL
        </label>
        <input
          type="url"
          value={photoUrl}
          onChange={e => setPhotoUrl(e.target.value)}
          placeholder="https://example.com/your-photo.jpg"
          style={{
            width: '100%', padding: '10px 12px', borderRadius: 8,
            border: '1.5px solid #E5E7EB', fontFamily: 'DM Sans, sans-serif',
            fontSize: 14, color: '#1A1A1A', background: '#fff', boxSizing: 'border-box',
          }}
        />
        <p style={{ fontSize: 12, color: '#9CA3AF', marginTop: 6 }}>
          Paste a direct image URL. A Gravatar or LinkedIn photo works great.
        </p>
      </div>

      <button
        onClick={handleSave}
        disabled={saving}
        className="cal-btn-primary"
        style={{
          padding: '10px 24px', borderRadius: 8, fontFamily: 'DM Sans, sans-serif',
          fontSize: 14, fontWeight: 600, cursor: saving ? 'wait' : 'pointer',
          background: saved ? '#16A34A' : '#D97706', color: '#fff', border: 'none',
          transition: 'background 0.2s',
        }}
      >
        {saved ? '✓ Saved' : saving ? 'Saving…' : 'Save profile'}
      </button>

      {error && (
        <p style={{ marginTop: 12, fontSize: 13, color: '#DC2626' }}>{error}</p>
      )}
    </section>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────
const S = {
  nav: {
    background: '#2C1810',
    padding: '0 16px',
    position: 'sticky',
    top: 0,
    zIndex: 100,
  },
  navInner: {
    maxWidth: 900,
    margin: '0 auto',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    height: 56,
    overflowX: 'auto',
  },
  navBrand: {
    fontFamily: "'Cormorant Garamond', Georgia, serif",
    color: '#B8860B',
    fontSize: '1.1rem',
    fontWeight: 600,
    marginRight: 12,
    whiteSpace: 'nowrap',
  },
  navBtn: (active) => ({
    background: active ? '#B8860B20' : 'transparent',
    border: 'none',
    color: active ? '#B8860B' : '#C8A870',
    padding: '6px 12px',
    borderRadius: 6,
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 13,
    fontWeight: active ? 600 : 400,
    whiteSpace: 'nowrap',
  }),
  signOutBtn: {
    marginLeft: 'auto',
    background: 'none',
    border: '1px solid #4A3020',
    color: '#C8A870',
    padding: '5px 12px',
    borderRadius: 6,
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 12,
    whiteSpace: 'nowrap',
  },

  section: {
    marginTop: 40,
  },
  sectionTitle: {
    fontFamily: "'Cormorant Garamond', Georgia, serif",
    fontSize: '1.5rem',
    fontWeight: 600,
    color: '#2C1810',
    margin: '0 0 20px',
  },
  loadingText: {
    color: '#A08060',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 14,
    padding: '20px 0',
  },
  emptyState: {
    color: '#A08060',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 14,
    padding: '32px 0',
    lineHeight: 1.6,
  },

  // Calendar card
  calCard: {
    background: '#FAF7F2',
    border: '1px solid #E0D8C8',
    borderRadius: 12,
    overflow: 'hidden',
  },
  calCardHead: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '16px 20px',
    cursor: 'pointer',
    userSelect: 'none',
  },
  calName: {
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 15,
    fontWeight: 600,
    color: '#2C1810',
  },
  slugDisplay: {
    fontFamily: 'monospace',
    fontSize: 12,
    color: '#6B5540',
    background: '#EDE9E3',
    padding: '2px 6px',
    borderRadius: 4,
  },
  copyBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#B8860B',
    fontSize: 14,
    padding: '2px 4px',
  },
  bookingCount: {
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 12,
    color: '#A08060',
    marginTop: 4,
  },
  calCardBody: {
    padding: '0 20px 20px',
    borderTop: '1px solid #EDE9E3',
  },
  editBtn: {
    background: 'none',
    border: '1px solid #C8B898',
    borderRadius: 6,
    padding: '8px 16px',
    color: '#6B5540',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 13,
    cursor: 'pointer',
    marginTop: 12,
  },
  formLabel: {
    display: 'block',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 12,
    fontWeight: 600,
    color: '#6B5540',
    marginBottom: 6,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
  formInput: {
    padding: '10px 12px',
    border: '1px solid #E0D8C8',
    borderRadius: 8,
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 14,
    color: '#2C1810',
    background: '#FFFFFF',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  },
  timeInput: {
    padding: '6px 8px',
    border: '1px solid #E0D8C8',
    borderRadius: 6,
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 13,
    color: '#2C1810',
    background: '#FFFFFF',
    outline: 'none',
  },
  removeBtn: {
    background: 'none',
    border: 'none',
    color: '#A08060',
    fontSize: 18,
    cursor: 'pointer',
    padding: '0 4px',
    lineHeight: 1,
  },
  addWindowBtn: {
    background: 'none',
    border: 'none',
    color: '#B8860B',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 12,
    cursor: 'pointer',
    padding: '2px 0',
    textAlign: 'left',
  },
  inlineError: {
    background: '#FEF2F2',
    border: '1px solid #FECACA',
    borderRadius: 8,
    padding: '10px 14px',
    color: '#B91C1C',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 13,
  },
  saveBtn: {
    background: '#B8860B',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 8,
    padding: '10px 20px',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },
  cancelEditBtn: {
    background: 'none',
    border: '1px solid #E0D8C8',
    borderRadius: 8,
    padding: '10px 16px',
    color: '#6B5540',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 13,
    cursor: 'pointer',
  },

  // Bookings
  filterRow: {
    display: 'flex',
    gap: 8,
    marginBottom: 16,
    flexWrap: 'wrap',
  },
  filterSelect: {
    padding: '8px 12px',
    border: '1px solid #E0D8C8',
    borderRadius: 8,
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 13,
    color: '#2C1810',
    background: '#FAF7F2',
    cursor: 'pointer',
    outline: 'none',
  },
  bookingRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    padding: '14px 16px',
    background: '#FAF7F2',
    border: '1px solid #E0D8C8',
    borderRadius: 10,
  },
  cancelBookingBtn: {
    background: 'none',
    border: '1px solid #FECACA',
    borderRadius: 6,
    padding: '6px 12px',
    color: '#B91C1C',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 12,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  cancelConfirmBtn: {
    background: '#B91C1C',
    color: '#FFF',
    border: 'none',
    borderRadius: 6,
    padding: '6px 12px',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 12,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  cancelDismissBtn: {
    background: 'none',
    border: '1px solid #E0D8C8',
    borderRadius: 6,
    padding: '6px 10px',
    color: '#6B5540',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 12,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    justifyContent: 'center',
    marginTop: 20,
  },
  pageBtn: (disabled) => ({
    background: 'none',
    border: '1px solid #E0D8C8',
    borderRadius: 6,
    padding: '8px 14px',
    color: disabled ? '#C8B898' : '#6B5540',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 13,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
  }),

  // Public link
  linkCard: {
    background: '#FAF7F2',
    border: '1px solid #E0D8C8',
    borderRadius: 12,
    padding: '24px',
  },
  publicUrl: {
    fontFamily: 'monospace',
    fontSize: 13,
    color: '#2C1810',
    background: '#EDE9E3',
    padding: '6px 10px',
    borderRadius: 6,
    wordBreak: 'break-all',
  },
  copyBtn2: {
    background: '#B8860B',
    color: '#FFF',
    border: 'none',
    borderRadius: 6,
    padding: '8px 14px',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  previewLink: {
    color: '#B8860B',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 13,
    textDecoration: 'none',
    fontWeight: 500,
    whiteSpace: 'nowrap',
  },
};
